import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions } from '../../utils/constants';
import CopyHistoricLayoutDialog from '../Dialogs/Dashboard/CopyHistoricLayoutDialog';
import HelpPopover from '../HelpPopover';

const { LIVE, HISTORIC } = ApplicationActions.DashboardViewMode;

const ROW_GAP_PX = 16;
const CSV_BUTTON_WIDTH_FALLBACK_PX = 168;
const COMPACT_TO_FULL_HYSTERESIS_PX = 16;

interface DashboardModeBarProps {
  runDate: number | null;
  sessions: Array<number> | null;
  onSessionChange: (runId: number | null) => void;
  onOpenCsv: () => void;
  onCopyHistoricLayout: () => void;
}

const DashboardModeBar = memo(({ runDate, sessions, onSessionChange, onOpenCsv, onCopyHistoricLayout }: DashboardModeBarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const leftControlsRef = useRef<HTMLDivElement>(null);
  const csvMeasureButtonRef = useRef<HTMLButtonElement>(null);
  const useCompactCsvButtonRef = useRef(false);
  const measureCompactCsvButtonRef = useRef<() => void>(() => {});
  const [useCompactCsvButton, setUseCompactCsvButton] = useState(false);
  const isHistoric = dashboardViewMode === HISTORIC;
  const showCsvDownload = isHistoric && Boolean(sessions?.length);
  const csvDownloadLabel = t('button.download.CSV', 'Download CSV Data');
  const sessionLabel = t('chart.session.time', 'Session');

  const selectValue = runDate != null && sessions?.includes(runDate) ? runDate : sessions?.length ? sessions[sessions.length - 1] : '';

  const handleModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
      if (!value || value === dashboardViewMode) return;
      dispatch(applicationSlice.actions.onSetDashboardViewMode(value as typeof LIVE | typeof HISTORIC));
    },
    [dashboardViewMode, dispatch]
  );

  const handleSessionChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const runId = Number(event.target.value);
      onSessionChange(Number.isFinite(runId) ? runId : null);
    },
    [onSessionChange]
  );

  const handleCopyClick = useCallback(() => {
    setCopyDialogOpen(true);
  }, []);

  const handleCopyCancel = useCallback(() => {
    setCopyDialogOpen(false);
  }, []);

  const handleCopyConfirm = useCallback(() => {
    onCopyHistoricLayout();
    setCopyDialogOpen(false);
  }, [onCopyHistoricLayout]);

  const measureCompactCsvButton = useCallback(() => {
    const row = rowRef.current;
    const leftControls = leftControlsRef.current;
    const measureButton = csvMeasureButtonRef.current;
    if (!row || !leftControls || !showCsvDownload) {
      useCompactCsvButtonRef.current = false;
      setUseCompactCsvButton(false);
      return;
    }

    const rowWidth = row.clientWidth;
    const isCompact = useCompactCsvButtonRef.current;
    const leftWidth = isCompact ? leftControls.getBoundingClientRect().width : leftControls.scrollWidth;
    const csvButtonWidth = measureButton?.getBoundingClientRect().width || CSV_BUTTON_WIDTH_FALLBACK_PX;
    const requiredWidth = leftWidth + csvButtonWidth + ROW_GAP_PX;

    const nextCompact = isCompact ? requiredWidth > rowWidth : requiredWidth > rowWidth + COMPACT_TO_FULL_HYSTERESIS_PX;

    if (nextCompact === useCompactCsvButtonRef.current) {
      return;
    }

    useCompactCsvButtonRef.current = nextCompact;
    setUseCompactCsvButton(nextCompact);
  }, [showCsvDownload]);

  measureCompactCsvButtonRef.current = measureCompactCsvButton;

  useLayoutEffect(() => {
    measureCompactCsvButtonRef.current();
    const frameId = requestAnimationFrame(() => measureCompactCsvButtonRef.current());
    return () => cancelAnimationFrame(frameId);
  }, [dashboardViewMode, sessions?.length, showCsvDownload]);

  useEffect(() => {
    const row = rowRef.current;
    const leftControls = leftControlsRef.current;
    if (!row) return;

    const resizeObserver = new ResizeObserver(() => {
      measureCompactCsvButtonRef.current();
    });

    resizeObserver.observe(row);
    if (leftControls) {
      resizeObserver.observe(leftControls);
    }

    let frameId: number | null = null;
    const scheduleMeasure = () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        measureCompactCsvButtonRef.current();
      });
    };

    window.addEventListener('resize', scheduleMeasure);
    window.visualViewport?.addEventListener('resize', scheduleMeasure);

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      window.visualViewport?.removeEventListener('resize', scheduleMeasure);
    };
  }, [showCsvDownload]);

  return (
    <>
      <Stack
        ref={rowRef}
        direction="row"
        alignItems="center"
        useFlexGap
        sx={{
          width: '100%',
          minWidth: 0,
          flexWrap: 'nowrap',
          gap: 2,
        }}
      >
        <Stack
          ref={leftControlsRef}
          direction="row"
          alignItems="center"
          spacing={1.5}
          useFlexGap
          sx={{
            flex: useCompactCsvButton ? '0 1 auto' : '1 1 auto',
            width: useCompactCsvButton ? 'fit-content' : undefined,
            maxWidth: useCompactCsvButton ? '100%' : undefined,
            minWidth: 0,
            flexWrap: 'nowrap',
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={dashboardViewMode}
            onChange={handleModeChange}
            aria-label={t('dashboard.mode.ariaLabel', 'Select dashboard view mode')}
            sx={(theme) => ({
              flexShrink: 0,
              borderRadius: 999,
              overflow: 'hidden',
              border: `1px solid ${theme.vars?.palette?.divider || theme.palette.divider}`,
              backgroundColor: theme.vars?.palette?.background?.default || theme.palette.background.default,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: 0,
                minHeight: 28,
                paddingInline: theme.spacing(1.25),
                paddingBlock: theme.spacing(0.375),
                color: theme.vars?.palette?.text?.secondary || theme.palette.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: 0,
                textTransform: 'none',
                '&:not(:first-of-type)': {
                  borderLeft: `1px solid ${theme.vars?.palette?.divider || theme.palette.divider}`,
                },
                '&:hover': {
                  backgroundColor: theme.vars?.palette?.action?.hover || theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  color: theme.vars?.palette?.text?.primary || theme.palette.text.primary,
                  backgroundColor: theme.vars?.palette?.background?.paper || theme.palette.background.paper,
                  fontWeight: 600,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: theme.vars?.palette?.background?.paper || theme.palette.background.paper,
                },
              },
            })}
          >
            <ToggleButton value={LIVE}>{t('dashboard.mode.live', 'Live')}</ToggleButton>
            <ToggleButton value={HISTORIC}>{t('dashboard.mode.historic', 'Historic')}</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flexShrink: 0 }}>
            <HelpPopover
              id="dashboard-mode-info-popover"
              ariaLabel={t('dashboard.mode.infoAriaLabel', 'About live and historic dashboard modes')}
              text={t(
                'dashboard.mode.info',
                'Live mode shows real-time sensor data on your editable dashboard. Historic mode lets you browse past program sessions and download CSV data.'
              )}
            />
          </Box>

          {isHistoric &&
            (sessions?.length ? (
              <>
                <FormControl
                  size="small"
                  sx={{
                    flex: useCompactCsvButton ? '0 1 auto' : '1 1 auto',
                    minWidth: 120,
                    maxWidth: 280,
                    flexShrink: 1,
                  }}
                >
                  <InputLabel id="session-select-label" shrink>
                    {sessionLabel}
                  </InputLabel>
                  <Select labelId="session-select-label" id="session-select" label={sessionLabel} onChange={handleSessionChange} value={selectValue}>
                    {sessions.map((session) => (
                      <MenuItem key={session} value={session}>
                        {t('session.time', { date: new Date(session) })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title={t('dashboard.copyHistoricLayout.button', 'Copy layout to live dashboard')}>
                  <IconButton
                    size="small"
                    aria-label={t('dashboard.copyHistoricLayout.button', 'Copy layout to live dashboard')}
                    onClick={handleCopyClick}
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                <Trans i18nKey="chart.session.run-to-save">Run and stop a program to save session history.</Trans>
              </Typography>
            ))}
        </Stack>

        {showCsvDownload ? (
          useCompactCsvButton ? (
            <Tooltip title={csvDownloadLabel}>
              <IconButton onClick={onOpenCsv} aria-label={csvDownloadLabel} size="small" sx={{ flexShrink: 0 }}>
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button variant="outlined" onClick={onOpenCsv} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Trans i18nKey="button.download.CSV">Download CSV Data</Trans>
            </Button>
          )
        ) : null}
      </Stack>

      {showCsvDownload ? (
        <Box aria-hidden sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', height: 0, overflow: 'hidden' }}>
          <Button ref={csvMeasureButtonRef} variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
            <Trans i18nKey="button.download.CSV">Download CSV Data</Trans>
          </Button>
        </Box>
      ) : null}

      <CopyHistoricLayoutDialog open={copyDialogOpen} onCancel={handleCopyCancel} onConfirm={handleCopyConfirm} />
    </>
  );
});

DashboardModeBar.displayName = 'DashboardModeBar';

export default DashboardModeBar;
